import { VoidCallback } from '@deqo/foundation';
import { default as React, useCallback, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import styled from 'styled-components';
import { promptName } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../../concepts/objects';
import { Icon } from '../../../../concepts/resources/Icon';
import { useBurden } from '../../../../platform/Hook';
import { DropButton } from '../../../../ui/DropButton';
import { Tip } from '../../../../ui/Tip';
import { useResolve } from '../../../Container';
import { EditorSession } from '../../../canvas/EditorSession';
import { MyProfileInfo } from '../../../canvas/MyProfileInfo';
import { SelectedVoteRound } from '../SelectedVoteRound';
import { VoteIndex } from '../VoteIndex';
import { deleteVoteRound } from '../VotingCommands';
import { resolveActiveVoteRound } from '../VotingHelpers';
import { CoolReadStore } from '../VotingViewState';
import { ActionItem, Box, Colors, Float, Prompt } from './../../../../ui';

const InfoBlock = styled.div<{ heading?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const InfoLabel = styled.div`
`;

const InfoValue = styled.div`
    padding-right: 0.75rem;
    text-align: right;
`;

const Divide = styled.div`
    height: .75rem;
`;

const LabelText = styled.div`
    color: ${Colors.primaryText};
    font-weight: 450;
`;

const initialState = {
    roundId: null,
    title: '',
    totalVotes: 0,
    yourVotes: 0,
    duplicates: false,
    allocation: 0,
};

class VoteRoundInfoViewModel extends CoolReadStore(initialState) {

    constructor(
        private profile: MyProfileInfo,
        private objects: ObjectManager,
        private session: EditorSession,
        private voteIndex: VoteIndex) {
        super();

        this.burden(
            objects.on('VoteRound', 'allocated', this.doUpdate),
            objects.on('VoteRound', 'released', this.doUpdate),
            voteIndex.on('updated', this.doUpdate),
            session.observe(SelectedVoteRound, this.doUpdate),
        );

        this.doUpdate();
    }

    private doUpdate = () => {
        const activeRound = resolveActiveVoteRound(this.objects, this.session);
        this.mutate(x => {
            x.roundId = activeRound?.id ?? null;
            x.title = activeRound?.label ?? '?';
            x.totalVotes = this.voteIndex.count(activeRound?.id ?? '', '*');
            x.yourVotes = this.voteIndex.count(activeRound?.id ?? '', this.profile.id);
            x.duplicates = activeRound?.duplicates;
            x.allocation = activeRound?.allocation ?? 0;
        });
    };
}

export const VoteRoundInfoPanel: LFC<{ dismiss: VoidCallback }> = ({ dismiss }) => {

    const session = useResolve(EditorSession);
    const objects = useResolve(ObjectManager);
    const voteIndex = useResolve(VoteIndex);
    const viewModel = useBurden(() => new VoteRoundInfoViewModel(session.get(MyProfileInfo), objects, session, voteIndex));

    const dispatch = useResolve(CommandDispatcher);
    const onDelete = useCallback(() => {
        dispatch(deleteVoteRound, [viewModel.roundId]);
        dismiss();
    }, [dismiss, viewModel.roundId]);

    return <Box width="25" padding="1">
        <InfoBlock >
            <LabelText>{viewModel.title}</LabelText>
            <Tip text="Delete Round" position="top">
                <DropButton rounding="full" onClick={onDelete}>
                    <Icon src="deqo://icons/hero/trash" />
                </DropButton>
            </Tip>
        </InfoBlock>
        <Divide />
        <InfoBlock>
            <InfoLabel>Total Votes Cast:</InfoLabel>
            <InfoValue>{viewModel.totalVotes}</InfoValue>
        </InfoBlock>
        <InfoBlock>
            <InfoLabel>Your Votes Cast:</InfoLabel>
            <InfoValue>{viewModel.yourVotes}</InfoValue>
        </InfoBlock>
        <Divide />
        <InfoBlock>
            <InfoLabel>Duplicates Allowed:</InfoLabel>
            <InfoValue>{viewModel.duplicates ? 'Yes' : 'No'}</InfoValue>
        </InfoBlock>
        <InfoBlock>
            <InfoLabel>Vote Allocation:</InfoLabel>
            <InfoValue>{viewModel.allocation || '∞'}</InfoValue>
        </InfoBlock>
    </Box>;
};

export const VoteRoundInfoActionItemWithPopper: LFC<{ label: string }> = ({ label }) => {

    const [open, setOpen] = useState(false);
    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);

    const pn = promptName();

    return <Manager>
        <Reference>
            {({ ref }) =>
                <ActionItem className={`ignore-${pn}`} ref={ref} grow pressable onClick={() => setOpen(x => !x)}>
                    {label}
                </ActionItem>
            }
        </Reference>
        {open && <Popper placement="top">
            {({ ref, style, placement, arrowProps }) => (
                <div ref={ref} style={style} data-placement={placement}>
                    <Prompt name={pn} onAction={onToggle}>
                        <Box padding="1">
                            <Float>
                                <VoteRoundInfoPanel dismiss={() => setOpen(false)} />
                            </Float>
                        </Box>
                    </Prompt>
                </div>
            )}
        </Popper>}
    </Manager>;
};
